import List from '@mui/material/List';
import { useAtom } from 'jotai';

import CheckProducts from '@/components/CheckProducts';
import CheckRequirements from '@/components/CheckRequirements';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { useFetchNewReportsCount } from '@/pages/Reports/Reports.store';
import { checkPermissions } from '@/services/permissions';
import { isLayoutMobileAtom } from '@/store/layout';
import userAtom from '@/store/user.store';

import { SidebarLinkButton } from '../SidebarLinkButton';
import { SidebarRouteButton } from '../SidebarRouteButton';
import { SidebarSubmenuButton } from '../SidebarSubmenuButton';
import useStyles from './SidebarMenu.styles';
import type { SidebarMenuProps } from './SidebarMenu.types';

function SidebarMenu(props: SidebarMenuProps) {
  const { menuItems } = props;
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [user] = useAtom(userAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);

  const { classes } = useStyles();

  useFetchNewReportsCount(); // Need to fetch new reports count for the badge

  return (
    <List className={classes.sidebarMenu} disablePadding>
      {menuItems.map((item) => {
        /* button type to display submenus */
        if (
          checkPermissions(item.permissions, user, selectedOrganization) &&
          'menu' in item
        ) {
          if (item.customRequirements) {
            return (
              <CheckRequirements
                key={item.label}
                custom={item.customRequirements}
              >
                <SidebarSubmenuButton item={item} />
              </CheckRequirements>
            );
          }
          if (item.requiredProducts) {
            return (
              <CheckProducts
                key={item.label}
                requiredProducts={item.requiredProducts}
              >
                <SidebarSubmenuButton item={item} />
              </CheckProducts>
            );
          }
          return <SidebarSubmenuButton key={item.label} item={item} />;
        }

        /* button type to change routes */
        if (
          checkPermissions(item.permissions, user, selectedOrganization) &&
          'pathTo' in item &&
          !('link' in item)
        ) {
          if (item.customRequirements) {
            return (
              <CheckRequirements
                key={item.label}
                custom={item.customRequirements}
              >
                <SidebarRouteButton item={item} />
              </CheckRequirements>
            );
          }
          if (item.requiredProducts) {
            return (
              <CheckProducts
                key={item.label}
                requiredProducts={item.requiredProducts}
              >
                <SidebarRouteButton item={item} />
              </CheckProducts>
            );
          }
          return <SidebarRouteButton key={item.label} item={item} />;
        }

        /* button type to navigate out of application */
        if (
          checkPermissions(item.permissions, user, selectedOrganization) &&
          'link' in item
        ) {
          if (isLayoutMobile || !item.mobileOnly) {
            if (item.customRequirements) {
              return (
                <CheckRequirements
                  key={item.label}
                  custom={item.customRequirements}
                >
                  <SidebarLinkButton item={item} />
                </CheckRequirements>
              );
            }
            if (item.requiredProducts) {
              return (
                <CheckProducts
                  key={item.label}
                  requiredProducts={item.requiredProducts}
                >
                  <SidebarLinkButton item={item} />
                </CheckProducts>
              );
            }
            return <SidebarLinkButton key={item.label} item={item} />;
          }
        }

        return null;
      })}
    </List>
  );
}

export default SidebarMenu;
